"use client";

import { useCallback } from "react";

import { ExportLanguage, generateChatCompletionsCode } from "@perceptron/api/codeGenerators";
import { strings } from "@perceptron/constants";

import ExportDialogShell from "@demo/components/ExportCode/ExportDialogShell";
import { type ExportLanguageOption } from "@demo/components/ExportCode/types";
import { useEditorRefStore } from "@demo/store/editor-ref";
import { useMediaStore } from "@demo/store/media";
import { useModelStore } from "@demo/store/model";

import { buildExportConfig } from "./buildExportConfig";

const LANGUAGES: ExportLanguageOption[] = [
  { value: "python", label: "Python", highlighter: "python" },
  { value: "curl", label: "cURL", highlighter: "json" },
];

/**
 * Compose's export: chat/completions snippets (Python + cURL). Reads the editor +
 * media + model stores, builds the request config, and feeds the shared dialog
 * shell. Registered per-mode in `components/ExportCode/registry`.
 */
const ComposeExport = () => {
  const getCode = useCallback((apiKey: string, language: string) => {
    const { selectedModel, outputFormat, temperature, enableReasoning, enableAudioInVideo } = useModelStore.getState();
    const { mediaSets } = useMediaStore.getState();
    const { getEditorContentAsJSON } = useEditorRefStore.getState();

    const config = buildExportConfig({
      mediaSets,
      getEditorContentAsJSON,
      apiKey,
      modelName: selectedModel.name,
      outputFormat: outputFormat.value,
      temperature,
      enableReasoning,
      enableAudioInVideo,
    });

    return generateChatCompletionsCode(config, language as ExportLanguage);
  }, []);

  const analyticsModel = useCallback(() => useModelStore.getState().selectedModel.name, []);

  return (
    <ExportDialogShell
      analyticsModel={analyticsModel}
      description={strings.exportCode.modes.compose.description}
      getCode={getCode}
      languages={LANGUAGES}
      title={strings.exportCode.modes.compose.title}
    />
  );
};

export default ComposeExport;
